import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Slider from 'react-slick';

import { CssModuleType } from '@utils/cssModules';

import currentCssModule from './Recommendations.module.scss';
import { CategoryData, IProductCard, Market, Wishlist } from '../../types';
import { fetchRecommendations } from '../../api/constructor';
import RecommendationProductCard from '@components/RecommendationProductCard';
import Typography from '../../kit/atoms/Typography';
import {
    getGa4Data,
    getPriceForMarket,
    getObjectByDefaultVariant
} from '../../helpers';

interface RecommendationsProps {
    isSlider?: boolean;
    isAutoplay: string;
    autoplaySpeed: string;
    isVertical?: boolean;
    count?: number;
    itemId?: string;
    podId: string;
    marketData: string;
    wishlistData: string;
    discountData: string;
    headingType: string;
    showOnNoResults: string;
}

const Recommendations: React.FC<RecommendationsProps> = ({
    isSlider,
    isAutoplay,
    autoplaySpeed,
    isVertical,
    podId,
    count,
    itemId,
    marketData,
    wishlistData,
    discountData,
    headingType,
    showOnNoResults
}) => {
    const st: CssModuleType = currentCssModule;
    const [products, setProducts] = useState<IProductCard[]>([]);
    const [market, setMarket] = useState<Market>();
    const [discount, setDiscount] = useState<string>('');
    const [heading, setHeading] = useState<string>('');
    const [resultId, setResultId] = useState<string>('');
    const [totalResults, setTotalResults] = useState<number>(0);
    const [categoryData, setCategoryData] = useState<CategoryData | null>(null);
    const [isHorizontal, setIsHorizontal] = useState<boolean>(false);
    const [wishlist, setWishlist] = useState<Wishlist[] | null>(null);
    const sliderRef = useRef(null);

    const sliderOptions = {
        autoplay: isAutoplay === 'True' ? true : false,
        slidesToShow: 4,
        slidesToScroll: 1,
        infinite: true,
        autoplaySpeed: parseInt(autoplaySpeed),

        responsive: [
            {
                breakpoint: 1080,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 2
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1
                }
            }
        ]
    };

    useEffect(() => {
        if (products?.length === 0) return;

        if (products.length <= 4 && !isVertical) setIsHorizontal(true);
    }, [products]);

    useEffect(() => {
        if (categoryData && products) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'view_item_list',
                ecommerce: {
                    item_list_id: categoryData.group_id,
                    item_list_name: categoryData.display_name,
                    items: products.map((item, ind) => {
                        return getGa4Data(
                            ind,
                            item.data,
                            item.value,
                            getPriceForMarket(item.data, market.MarketName),
                            item.data.Increment,
                            categoryData
                        );
                    })
                }
            });
        }
    }, [categoryData, products]);

    const initComponent = () => {
        marketData && setMarket(JSON.parse(marketData));
        wishlistData && setWishlist(JSON.parse(wishlistData));
        discountData && setDiscount(JSON.parse(discountData));
        const url = new URL(window.location.href);
        const query = url.searchParams.get('search');

        const props = {};
        const newObj = {
            ...props,
            ...(query !== null && query !== undefined ? { term: query } : {}),
            ...(itemId !== null && itemId !== undefined
                ? { itemIds: itemId }
                : {}),
            ...(count !== null && count !== undefined
                ? { numResults: count }
                : {})
        };

        fetchRecommendations(podId, { ...newObj }).then((data) => {
            setProducts(data.response.results);
            setHeading(data.response.pod.display_name);
            setResultId(data.result_id);
            setTotalResults(data.response.total_num_results);
            setCategoryData({
                display_name: heading,
                group_id: heading
            });
        });
    };

    const watchElement = (element) => {
        if (!element) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (element.getAttribute(mutation.attributeName) === 'true') {
                    initComponent();
                }
            });
        });

        const config = {
            attributes: true,
            attributeOldValue: true,
            attributeFilter: ['init-recommendations']
        };

        observer.observe(element, config);
    };

    useEffect(() => {
        if (showOnNoResults === 'true') {
            const noResultsRecommendations = document.querySelector(
                '.product-recommendations-block.no-results > div'
            );
            watchElement(noResultsRecommendations);

            return;
        }

        initComponent();
    }, []);

    const handleWishlist = (variation_id: string) => {
        setWishlist((prevWishlist) => {
            if (!prevWishlist) return null;

            const itemIndex = prevWishlist.findIndex(
                (item) => item.Code === variation_id
            );

            if (itemIndex !== -1) {
                return [
                    ...prevWishlist.slice(0, itemIndex),
                    ...prevWishlist.slice(itemIndex + 1)
                ];
            } else {
                return [...prevWishlist, { Code: variation_id, Quantity: '1' }];
            }
        });
    };

    const getHeadingType = (headingType) => {
        const types = {
            'best-seller': st['heading--big'],
            'cross-sell-products': st['heading--small'],
            'cross-sell-products--vertical': st['heading--small-vertical']
        };

        return types[headingType];
    };

    const renderRecommendations = () => {
        if (products.length > 4 && isSlider)
            return (
                <Slider
                    {...sliderOptions}
                    ref={sliderRef}
                    className='recommendations-slider'
                >
                    {products?.map((item) => (
                        <RecommendationProductCard
                            className={st.slide}
                            key={`product_${item.data?.SKU}`}
                            productData={getObjectByDefaultVariant(item)}
                            data={item}
                            variationsQty={item.variations.length}
                            discount={discount}
                            handleWishlist={handleWishlist}
                            market={market}
                            wishlist={wishlist}
                            isInSlider={isSlider}
                            isShortVersion={isVertical}
                            noMargins={!isSlider && !isVertical}
                            categoryData={categoryData}
                        />
                    ))}
                </Slider>
            );

        return products?.map((item) => (
            <RecommendationProductCard
                key={`product_${item.data?.SKU}`}
                productData={getObjectByDefaultVariant(item)}
                data={item}
                variationsQty={item.variations.length}
                discount={discount}
                handleWishlist={handleWishlist}
                market={market}
                wishlist={wishlist}
                isInSlider={isSlider}
                isShortVersion={isVertical}
                noMargins={!isSlider && !isVertical}
                categoryData={categoryData}
            />
        ));
    };

    return (
        <div className={clsx(st.recommendations)}>
            {heading ? (
                <Typography
                    variant='h3'
                    className={clsx(
                        st.heading,
                        headingType && getHeadingType(headingType),
                        isVertical && st.vertical
                    )}
                >
                    {heading}
                </Typography>
            ) : null}
            <div
                className={clsx(
                    st['products-wrapper'],
                    isVertical && st.vertical,
                    isHorizontal && st.horizontal
                )}
                data-cnstrc-recommendations=''
                data-cnstrc-recommendations-pod-id={podId}
                data-cnstrc-result-id={resultId}
                data-cnstrc-num-results={totalResults}
            >
                {renderRecommendations()}
            </div>
        </div>
    );
};

export default Recommendations;
